import React, { useEffect, useRef, useState, MouseEvent } from "react";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { MatrixHero } from "../../common/components/MatrixHero";
import ClubLogosBar, { CLUB_LOGOS } from "../../common/components/ClubLogosBar";
import "../../scss/clubs.scss";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";

// ==================== HOOKS ====================

function useInView(threshold = 0.15) {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (ref.current) observer.unobserve(ref.current);
                }
            },
            { threshold }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return { ref, inView };
}

// ==================== DATA ====================

// Mapa logotypów z centralnej listy kół
const CLUB_LOGO_MAP: Record<string, string> = {};
CLUB_LOGOS.forEach((c) => {
    CLUB_LOGO_MAP[c.name] = c.image;
});

interface ClubData {
    acronym: string;
    fullName: string;
    descKey: TranslationKey;
    focusKeys: TranslationKey[];
}

const CLUBS: ClubData[] = [
    {
        acronym: "AEGIS",
        fullName: "Advanced Exploration Group for Information Security",
        descKey: "clubs_aegis_desc",
        focusKeys: ["clubs_aegis_focus_1", "clubs_aegis_focus_2", "clubs_aegis_focus_3", "clubs_aegis_focus_4"],
    },
    {
        acronym: "CONECT",
        fullName: "Community of Networking Engineers and Computing Technologies",
        descKey: "clubs_conect_desc",
        focusKeys: ["clubs_conect_focus_1", "clubs_conect_focus_2", "clubs_conect_focus_3", "clubs_conect_focus_4"],
    },
    {
        acronym: "NODE",
        fullName: "Networked Objects & Device Engineering",
        descKey: "clubs_node_desc",
        focusKeys: ["clubs_node_focus_1", "clubs_node_focus_2", "clubs_node_focus_3", "clubs_node_focus_4"],
    },
];

// ==================== COMPONENT ====================

const ClubsPage: React.FC = () => {
    const [ready, setReady] = useState(false);
    const { t } = useI18n();
    const location = useLocation();

    const { ref: refClub0, inView: inViewClub0 } = useInView(0.12);
    const { ref: refClub1, inView: inViewClub1 } = useInView(0.12);
    const { ref: refClub2, inView: inViewClub2 } = useInView(0.12);
    const clubRefs = [
        { ref: refClub0, inView: inViewClub0 },
        { ref: refClub1, inView: inViewClub1 },
        { ref: refClub2, inView: inViewClub2 },
    ];
    const { ref: refLogos, inView: inViewLogos } = useInView(0.15);

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    /* scroll to club section from hash */
    useEffect(() => {
        const hash = location.hash.replace("#", "");
        if (!hash) return;
        const timer = setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) {
                const navHeight = 70;
                const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
                window.scrollTo({ top, behavior: "smooth" });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [location.hash]);

    const handleMouseMove = (e: MouseEvent<HTMLElement>) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        target.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        target.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    };

    return (
        <section
            className={classNames("clubs-page", { "clubs-page--ready": ready })}
            onMouseMove={handleMouseMove}
            aria-label={t("clubs_section_label")}
        >
            <div className="grid-overlay" aria-hidden="true" />
            <div className="grid-glow" aria-hidden="true" />

            {/* ===== HERO ===== */}
            <MatrixHero
                title={t("clubs_hero_title")}
                subtitle={t("clubs_hero_subtitle")}
                density={0.5}
            />

            <div className="clubs-page__inner">

                {/* ===== LOGOS BAR ===== */}
                <div
                    ref={refLogos}
                    className={classNames("clubs-logos-wrap", { "clubs-logos-wrap--visible": inViewLogos })}
                >
                    <ClubLogosBar speed={25} />
                </div>

                {/* ===== CLUB SECTIONS ===== */}
                {CLUBS.map((club, idx) => {
                    const { ref, inView } = clubRefs[idx];
                    return (
                        <article
                            key={club.acronym}
                            id={club.acronym}
                            ref={ref}
                            className={classNames("club-section", { "club-section--visible": inView })}
                        >
                            <div className="club-section__glow" aria-hidden="true" />

                            {/* Logo + name header */}
                            <div className="club-section__header">
                                <img
                                    src={CLUB_LOGO_MAP[club.acronym]}
                                    alt={`${t("clubs_logo_alt")} ${club.acronym}`}
                                    className="club-section__logo"
                                />
                                <div className="club-section__names">
                                    <h2 className="club-section__acronym">{club.acronym}</h2>
                                    <p className="club-section__fullname">{club.fullName}</p>
                                </div>
                            </div>

                            {/* Description */}
                            <p className="club-section__desc">{t(club.descKey)}</p>

                            {/* Focus list */}
                            <ul className="club-focuses">
                                {club.focusKeys.map((key, i) => (
                                    <li
                                        key={i}
                                        className="club-focuses__item"
                                        style={{ animationDelay: `${i * 0.08}s` }}
                                    >
                                        {t(key)}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    );
                })}

            </div>
        </section>
    );
};

export default ClubsPage;
